import { Injectable } from '@angular/core';
import { MediaService } from './media.service';
import { RecordingStorageService } from './recording-storage.service';
import { AIService } from './ai.service';
import { AudioSegment, FailedRecording } from '../interfaces/recording-storage.interface';
import { firstValueFrom, Subject, Subscription, takeUntil, takeWhile } from 'rxjs';
import * as moment from 'moment';

export interface RetryStatusUpdate {
  recordingId: string;
  status: string;
  message: string;
}

export interface RetryResult {
  recordingId: string;
  success: boolean;
  failedSegments: string[];
  error?: string;
  finalResult?: any; // AI-generated notes result (same as ai-dialog's finalResult)
  requestId?: string; // AI request ID
}

@Injectable({
  providedIn: 'root'
})
export class RecordingRetryService {
  private activeRetries = new Map<string, { 
    subscription?: Subscription; 
    statusSubject: Subject<RetryStatusUpdate>;
    transcriptionSubscription?: Subscription;
    aiSubscription?: Subscription;
    destroy$: Subject<void>;
  }>();
  private readonly MAX_RETRIES = 5;
  private readonly SEGMENT_TIMEOUT_MS = 60000; // 60 seconds
  private readonly AI_STATUS_TIMEOUT_MS = 300000; // 5 minutes (matching ai-dialog)

  constructor(
    private mediaService: MediaService,
    private recordingStorageService: RecordingStorageService,
    private aiService: AIService
  ) {}

  // Retry recording following the same flow as ai-dialog
  async retryRecording(
    recordingId: string,
    patientId: string,
    groupId: string,
    userId: string,
    type: 'general' | 'ward-round' = 'general',
    startTime?: string,
    finishTime?: string,
    onStatusUpdate?: (update: RetryStatusUpdate) => void
  ): Promise<RetryResult> {
    // Prevent multiple retries for the same recording (single processing lock)
    if (this.activeRetries.has(recordingId)) {
      throw new Error('Recording is already being retried');
    }

    // Prevent multiple simultaneous retries (only one at a time)
    if (this.activeRetries.size > 0) {
      throw new Error('Another recording is currently being retried. Please wait for it to complete.');
    }

    const statusSubject = new Subject<RetryStatusUpdate>();
    const destroy$ = new Subject<void>();
    let transcriptionSubscription: Subscription | undefined;
    let aiSubscription: Subscription | undefined;

    // Generate a new jobId for this retry attempt (same as ai-dialog)
    const jobId = this.aiService.generateRandomSocketId(20);
    console.log(`[RetryService] Generated new jobId for retry: ${jobId}`);

    // Store in active retries (will be cleaned up in finally)
    this.activeRetries.set(recordingId, {
      subscription: undefined, // Will be set later when transcription subscription is created
      statusSubject,
      destroy$
    });

    // A retry writes a segment per transcript; each write would otherwise schedule a scan of
    // the entire segments store. Suspend it and let the finally block run a single refresh.
    this.recordingStorageService.pauseFailedRecordingsRefresh();

    try {
      console.log(`[RetryService] Starting retry for recording: ${recordingId}`);
      console.log(`[RetryService] Patient: ${patientId}, Group: ${groupId}, Type: ${type}`);
      console.log(`[RetryService] Using jobId: ${jobId} for this retry attempt`);
      
      // Try to get recording from IndexedDB
      let recording = await this.recordingStorageService.getRecording(recordingId);
      
      // If recording doesn't exist, it might be a virtual recording from orphaned segments
      // Create it from the segments
      if (!recording) {
        console.log(`[RetryService] Recording not found in IndexedDB, checking for orphaned segments...`);
        const segments = await this.recordingStorageService.getRecordingSegments(recordingId);
        
        if (segments.length === 0) {
          console.error(`[RetryService] No segments found for recording ${recordingId}`);
          throw new Error(`Recording ${recordingId} not found and no segments available`);
        }
        
        // Create virtual recording from segments (same logic as getRecordingsWithFailedSegments)
        const firstSegment = segments[0];
        if (!firstSegment.patientId || !firstSegment.groupId || !firstSegment.userId) {
          console.error(`[RetryService] Segments missing required metadata (patientId, groupId, or userId)`);
          throw new Error(`Recording ${recordingId} segments are missing required metadata`);
        }
        
        // Calculate total duration from segment sizes (rough estimate)
        const totalSize = segments.reduce((sum, seg) => sum + (seg.size || 0), 0);
        const estimatedDurationSeconds = Math.round(totalSize / (1024 * 1024) * 60);
        
        // Get failed segment IDs
        const failedSegmentIds = segments
          .filter(seg => !seg.uploadSuccess || seg.transcribeFailed || !seg.processed)
          .map(seg => seg.id);
        
        // Determine status based on segment failures
        const hasUploadFailures = segments.some(seg => !seg.uploadSuccess);
        const hasTranscriptionFailures = segments.some(seg => seg.transcribeFailed);
        const allProcessed = segments.every(seg => seg.processed || seg.retryAttempts >= 5);
        
        recording = {
          id: recordingId,
          jobId: recordingId,
          sessionId: recordingId,
          patientId: firstSegment.patientId,
          patientName: firstSegment.patientName,
          groupId: firstSegment.groupId,
          userId: firstSegment.userId || userId, // Use provided userId as fallback
          type: firstSegment.type || type || 'general',
          segmentIds: segments.map(seg => seg.id),
          totalDuration: estimatedDurationSeconds,
          startTime: firstSegment.startTime || startTime,
          finishTime: firstSegment.finishTime || finishTime,
          status: hasTranscriptionFailures || !allProcessed ? 'failed' : 'pending',
          uploadFailed: hasUploadFailures,
          transcriptionFailed: hasTranscriptionFailures,
          failedSegments: failedSegmentIds,
          createdAt: new Date().toISOString(),
          version: 1
        };
        
        console.log(`[RetryService] Created virtual recording from ${segments.length} orphaned segment(s)`);
        
        // Store the virtual recording in IndexedDB so it becomes a real recording
        // This ensures all subsequent operations (status updates, etc.) work correctly
        recording.status = 'retrying';
        await this.recordingStorageService.storeRecording(recording);
        console.log(`[RetryService] Stored virtual recording in IndexedDB for retry`);
      }

      console.log(`[RetryService] Recording found:`, {
        id: recording.id,
        status: recording.status,
        segmentCount: recording.segmentIds?.length || 0,
        uploadFailed: recording.uploadFailed,
        transcriptionFailed: recording.transcriptionFailed
      });

      // Subscribe to status updates if callback provided
      if (onStatusUpdate) {
        statusSubject.subscribe(onStatusUpdate);
      }

      this.emitStatus(statusSubject, recordingId, 'Uploading segments...', 'Uploading failed segments to Azure...');
      console.log(`[RetryService] Status: Uploading failed segments to Azure...`);

      // Update status to retrying
      await this.recordingStorageService.updateRecordingStatus(recordingId, 'retrying');
      console.log(`[RetryService] Recording status updated to 'retrying'`);

      // Get all segments for this recording
      const segments = await this.recordingStorageService.getRecordingSegments(recordingId);
      console.log(`[RetryService] Retrieved ${segments.length} segment(s)`);

      // Filter to only failed segments (or all if none succeeded)
      const failedSegments = segments.filter(seg => 
        !seg.uploadSuccess || seg.transcribeFailed || !seg.processed
      );
      console.log(`[RetryService] Found ${failedSegments.length} failed segment(s) out of ${segments.length} total`);

      // CRITICAL: Subscribe to transcription updates BEFORE uploading segments
      // This ensures the socket joins the room before transcription jobs are queued
      // (Same pattern as ai-dialog: subscribe BEFORE recording/upload starts)
      console.log(`[RetryService] Step 0: Subscribing to transcription updates (before uploads)`);
      this.emitStatus(statusSubject, recordingId, 'Preparing...', 'Setting up transcription listener...');
      
      // Subscribe to transcription results via socket using the new jobId (same as ai-dialog)
      this.mediaService.subscribeToTranscriptionResults(jobId);
      console.log(`[RetryService] Subscribed to transcription results for jobId: ${jobId}`);
      
      // Subscribe to transcriptionStatus$ observable (same as ai-dialog line 308-316)
      transcriptionSubscription = this.mediaService.transcriptionStatus$
        .pipe(takeUntil(destroy$))
        .subscribe((result: any) => {
          if (result) {
            console.log(`[RetryService] ✅ Transcription received:`, {
              jobId: result.jobId,
              socketId: result.socketId,
              transcriptLength: result.transcript?.length || 0
            });
            // In ai-dialog, result.jobId is the segmentId (socketId), not the recordingId
            // The recordingId is passed as context (this.jobId in ai-dialog)
            this.handleTranscriptionResponse(recordingId, result.jobId || result.socketId, result.transcript, segments, statusSubject);
          }
        });
      
      // Update active retries with subscriptions
      const activeRetry = this.activeRetries.get(recordingId);
      if (activeRetry) {
        activeRetry.subscription = transcriptionSubscription;
        activeRetry.transcriptionSubscription = transcriptionSubscription;
      }

      if (failedSegments.length === 0) {
        console.log(`[RetryService] All segments already succeeded, proceeding to AI processing`);
        // All segments already succeeded, check if we need to compile and process
        const aiResult = await this.processIfAllSegmentsReady(recordingId, recording, patientId, groupId, type, startTime, finishTime, statusSubject, destroy$, jobId);
        console.log(`[RetryService] AI processing completed:`, { 
          hasFinalResult: !!aiResult.finalResult,
          requestId: aiResult.requestId 
        });
        
        await this.recordingStorageService.updateRecordingStatus(recordingId, 'completed');
        
        // Store finalResult if available
        if (aiResult.finalResult) {
          const updatedRecording = await this.recordingStorageService.getRecording(recordingId);
          if (updatedRecording) {
            updatedRecording.finalResult = aiResult.finalResult;
            updatedRecording.requestId = aiResult.requestId;
            await this.recordingStorageService.storeRecording(updatedRecording);
            console.log(`[RetryService] Stored finalResult in recording metadata`);
          }
        }
        
        console.log(`[RetryService] Retry completed successfully (no failed segments)`);
        return { 
          recordingId,
          success: true, 
          failedSegments: [],
          finalResult: aiResult.finalResult,
          requestId: aiResult.requestId
        } as RetryResult;
      }

      // Step 1: Retry upload for failed segments
      console.log(`[RetryService] Step 1: Retrying upload for ${failedSegments.length} segment(s)`);
      this.emitStatus(statusSubject, recordingId, 'Uploading segments...', `Uploading ${failedSegments.length} segment(s)...`);
      await this.retrySegmentUploads(failedSegments, jobId, patientId, groupId, userId);
      console.log(`[RetryService] Step 1 completed: Segment uploads retried`);

      // Step 2: Wait for transcriptions (socket already subscribed above)
      console.log(`[RetryService] Step 2: Waiting for transcriptions...`);
      this.emitStatus(statusSubject, recordingId, 'Waiting for transcriptions...', 'Waiting for segments to be transcribed...');

      // Step 3: Wait for all segments to be processed
      console.log(`[RetryService] Step 3: Waiting for all segments to be processed`);
      const allProcessed = await this.waitForAllSegmentsProcessed(segments, destroy$);
      console.log(`[RetryService] Step 3 completed: All segments processed = ${allProcessed}`);

      if (!allProcessed) {
        const stillFailed = segments.filter(seg => !seg.processed && seg.retryAttempts >= this.MAX_RETRIES);
        const failedSegmentIds = stillFailed.map(seg => seg.id);
        console.warn(`[RetryService] Some segments failed after maximum retries:`, failedSegmentIds);
        
        await this.recordingStorageService.updateRecordingStatus(recordingId, 'failed');
        const updatedRecording = await this.recordingStorageService.getRecording(recordingId);
        if (updatedRecording) {
          updatedRecording.failedSegments = failedSegmentIds;
          await this.recordingStorageService.storeRecording(updatedRecording);
        }
        
        destroy$.next();
        destroy$.complete();
      return {
        recordingId,
        success: false,
        failedSegments: failedSegmentIds,
        error: 'Some segments failed after maximum retries'
      } as RetryResult;
      }

      // Step 4: Compile transcript and submit for AI processing
      console.log(`[RetryService] Step 4: Compiling transcript and submitting for AI processing`);
      const aiResult = await this.processIfAllSegmentsReady(recordingId, recording, patientId, groupId, type, startTime, finishTime, statusSubject, destroy$, jobId);
      console.log(`[RetryService] Step 4 completed: AI processing finished`, {
        hasFinalResult: !!aiResult.finalResult,
        requestId: aiResult.requestId
      });

      destroy$.next();
      destroy$.complete();

      // Mark as completed and store final result
      await this.recordingStorageService.updateRecordingStatus(recordingId, 'completed');
      console.log(`[RetryService] Recording status updated to 'completed'`);
      
      // Store finalResult in recording metadata (so dialog can show View button)
      if (aiResult.finalResult) {
        const updatedRecording = await this.recordingStorageService.getRecording(recordingId);
        if (updatedRecording) {
          updatedRecording.finalResult = aiResult.finalResult;
          updatedRecording.requestId = aiResult.requestId;
          await this.recordingStorageService.storeRecording(updatedRecording);
          console.log(`[RetryService] Stored finalResult in recording metadata`);
        }
      }

      console.log(`[RetryService] ✅ Retry completed successfully`);
      return { 
        recordingId,
        success: true, 
        failedSegments: [],
        finalResult: aiResult.finalResult,
        requestId: aiResult.requestId
      } as RetryResult;

    } catch (error) {
      console.error(`[RetryService] ❌ Error retrying recording:`, error);
      await this.recordingStorageService.updateRecordingStatus(recordingId, 'failed');
      
      destroy$.next();
      destroy$.complete();
      
      return {
        recordingId,
        success: false,
        failedSegments: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      } as RetryResult;
    } finally {
      // Cleanup all subscriptions (following ai-dialog pattern)
      const activeRetry = this.activeRetries.get(recordingId);
      if (activeRetry) {
        if (activeRetry.transcriptionSubscription && typeof activeRetry.transcriptionSubscription.unsubscribe === 'function') {
          activeRetry.transcriptionSubscription.unsubscribe();
        }
        if (activeRetry.aiSubscription && typeof activeRetry.aiSubscription.unsubscribe === 'function') {
          activeRetry.aiSubscription.unsubscribe();
        }
        if (activeRetry.subscription && typeof activeRetry.subscription.unsubscribe === 'function') {
          activeRetry.subscription.unsubscribe();
        }
        activeRetry.destroy$.next();
        activeRetry.destroy$.complete();
        activeRetry.statusSubject.complete();
      }
      this.activeRetries.delete(recordingId);
      destroy$.next();
      destroy$.complete();
      statusSubject.complete();
      this.recordingStorageService.resumeFailedRecordingsRefresh();
    }
  }

  private async retrySegmentUploads(
    segments: AudioSegment[],
    jobId: string,
    patientId: string,
    groupId: string,
    userId: string
  ): Promise<void> {
    console.log(`[RetryService] Retrying upload for ${segments.length} segment(s)`);
    const results = await Promise.allSettled(
      segments.map((segment, index) => {
        console.log(`[RetryService] Retrying segment ${index + 1}/${segments.length}: ${segment.id}`);
        return this.retrySegmentUpload(segment, jobId, patientId, groupId, userId);
      })
    );

    // Update segment statuses (no need to re-store in IndexedDB - we're already working from there)
    let successCount = 0;
    let failureCount = 0;
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const segment = segments[i];
      
      if (result.status === 'fulfilled' && result.value) {
        segment.uploadSuccess = true;
        segment.transcribeFailed = false;
        successCount++;
        console.log(`[RetryService] ✅ Segment ${segment.id} uploaded successfully`);
      } else {
        segment.transcribeFailed = true;
        segment.retryAttempts = (segment.retryAttempts || 0) + 1;
        failureCount++;
        console.warn(`[RetryService] ⚠️ Segment ${segment.id} upload failed:`, result.status === 'rejected' ? result.reason : 'Unknown error');
      }
      
      // Note: We don't re-store segments in IndexedDB since we're already working from there
      // The segment objects are updated in memory and will be persisted when the recording is updated
    }
    console.log(`[RetryService] Upload retry complete: ${successCount} succeeded, ${failureCount} failed`);
  }

  private async retrySegmentUpload(
    segment: AudioSegment,
    jobId: string,
    patientId: string,
    groupId: string,
    userId: string
  ): Promise<boolean> {
    try {
      console.log(`[RetryService] Retrying segment upload: ${segment.id} (${segment.fileName}, ${segment.size} bytes)`);
      const audioFile = new File([segment.blob], segment.fileName, { type: segment.mimeType });

      // Step 1: Fetch upload URL (use jobId for the upload URL request)
      console.log(`[RetryService] Fetching upload URL for segment ${segment.id}`);
      const { uploadUrl, blobName, containerName } = await firstValueFrom(
        this.mediaService.getUploadUrl(patientId, jobId, segment.fileName)
      ) as any;
      console.log(`[RetryService] Upload URL received:`, { blobName, containerName });

      // Step 2: Upload file
      console.log(`[RetryService] Uploading segment ${segment.id} to Azure`);
      await firstValueFrom(this.mediaService.uploadFileToAzure(audioFile, uploadUrl)) as any;
      console.log(`[RetryService] Segment ${segment.id} uploaded successfully`);

      // Step 3: Notify backend to queue transcription (use jobId as the first parameter)
      console.log(`[RetryService] Notifying server to queue transcription for segment ${segment.id}`);
      await firstValueFrom(
        this.mediaService.notifyServerFileUploaded(
          jobId,
          segment.id,
          blobName,
          containerName,
          patientId,
          groupId,
          userId,
          'segment'
        )
      ) as any;
      console.log(`[RetryService] Transcription queued for segment ${segment.id}`);

      // Update segment status (in memory only - no need to re-store in IndexedDB)
      segment.uploadSuccess = true;
      segment.blobName = blobName;
      segment.containerName = containerName;
      segment.transcribeFailed = false;

      return true;
    } catch (error) {
      console.error(`[RetryService] Error retrying segment ${segment.id}:`, error);
      segment.transcribeFailed = true;
      segment.retryAttempts = (segment.retryAttempts || 0) + 1;
      return false;
    }
  }

  // Handle transcription response (following ai-dialog pattern - line 470-502)
  private handleTranscriptionResponse(
    recordingId: string,
    segmentId: string, // This is the socketId/segmentId (same as ai-dialog's jobId parameter)
    transcript: string,
    segments: AudioSegment[],
    statusSubject: Subject<RetryStatusUpdate>
  ): void {
    console.log(`[RetryService] ✅ Received transcription for Segment ID: ${segmentId}`, {
      transcriptLength: transcript?.length || 0,
      totalSegments: segments.length
    });

    const segment = segments.find(seg => seg.id === segmentId);
    if (segment) {
      segment.transcript = transcript;
      segment.processed = true;
      segment.transcribeFailed = false;
      
      console.log(`[RetryService] Updated segment ${segmentId} with transcript (${transcript.length} chars)`);
      
      // Persist transcript to IndexedDB so compileTranscript can read it.
      // Metadata-only update: storeSegment() would re-encode and rewrite the whole audio
      // payload for every transcribed segment, and on iOS could overwrite good audio with an
      // empty buffer if the rehydrated blob is dead.
      this.recordingStorageService.updateSegmentMetadata(segment.id, {
        transcript,
        processed: true,
        transcribeFailed: false
      }).catch(err =>
        console.warn(`[RetryService] Failed to persist segment transcript:`, err)
      );

      const remaining = segments.filter(seg => !seg.processed && seg.retryAttempts < this.MAX_RETRIES).length;
      const processedCount = segments.filter(seg => seg.processed).length;
      console.log(`[RetryService] Transcription progress: ${processedCount}/${segments.length} processed, ${remaining} remaining`);
      
      if (remaining > 0) {
        this.emitStatus(statusSubject, recordingId, 'Transcribing...', `${remaining} segment(s) remaining...`);
      }
    } else {
      console.warn(`[RetryService] ⚠️ No matching segment found for segmentId: ${segmentId}`);
    }
  }

  private async waitForAllSegmentsProcessed(
    segments: AudioSegment[],
    destroy$: Subject<void>
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const pending = segments.filter(seg => !seg.processed && seg.retryAttempts < this.MAX_RETRIES);
        
        if (pending.length === 0) {
          clearInterval(checkInterval);
          resolve(true);
        }

        // Check for exhausted retries
        const exhausted = segments.filter(seg => !seg.processed && seg.retryAttempts >= this.MAX_RETRIES);
        if (exhausted.length > 0 && pending.length === 0) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 1000);

      // Cleanup on destroy
      destroy$.subscribe(() => {
        clearInterval(checkInterval);
      });
    });
  }

  private async processIfAllSegmentsReady(
    recordingId: string,
    recording: FailedRecording,
    patientId: string,
    groupId: string,
    type: 'general' | 'ward-round',
    startTime: string | undefined,
    finishTime: string | undefined,
    statusSubject: Subject<RetryStatusUpdate>,
    destroy$: Subject<void>,
    jobId: string
  ): Promise<{ finalResult?: any; requestId?: string }> {
    // Compile transcript (following ai-dialog pattern - line 1468-1474)
    const transcript = await this.compileTranscript(recordingId);
    
    if (!transcript || transcript.trim().length === 0) {
      throw new Error('No transcript available to process');
    }

    this.emitStatus(statusSubject, recordingId, 'Processing with AI...', 'Compiling transcript and submitting for AI processing...');

    // Submit for AI processing (following ai-dialog pattern - line 1503-1524)
    this.aiService.isProcessing.next(true);
    const isWardRound = type === 'ward-round';
    
    // Subscribe to AI status updates and wait for completion (same as ai-dialog line 239-279)
    // The subscription happens inside waitForAICompletion, then we call generateNotesFromAudio
    const aiCompletionPromise = this.waitForAICompletion(recordingId, statusSubject, destroy$);
    
    // Now call generateNotesFromAudio (same as ai-dialog line 1508)
    this.aiService.generateNotesFromAudio(
      null,
      false, // basicOutput (ai-dialog uses !this.aiGenerate, but we default to false for retries)
      patientId,
      isWardRound,
      transcript,
      recordingId,
      startTime || recording.startTime,
      finishTime || recording.finishTime
    ).subscribe(
      (result: any) => {
        console.log('✅ AI Notes generation started');
      },
      (error) => {
        console.error('❌ Error starting AI processing:', error);
        this.aiService.isProcessing.next(false);
        // Cancel the AI subscription wait
        const activeRetry = this.activeRetries.get(recordingId);
        if (activeRetry && activeRetry.aiSubscription) {
          activeRetry.aiSubscription.unsubscribe();
        }
        throw error;
      }
    );

    // Wait for AI completion (subscription already set up in waitForAICompletion)
    return await aiCompletionPromise;
  }

  // Wait for AI completion (following ai-dialog pattern - line 239-279)
  private async waitForAICompletion(
    recordingId: string,
    statusSubject: Subject<RetryStatusUpdate>,
    destroy$: Subject<void>
  ): Promise<{ finalResult?: any; requestId?: string }> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        subscription.unsubscribe();
        this.aiService.isProcessing.next(false);
        reject(new Error('AI processing timeout'));
      }, this.AI_STATUS_TIMEOUT_MS);

      // Subscribe to aiStatus$ (same as ai-dialog line 239-279)
      const subscription = this.aiService.aiStatus$
        .pipe(
          takeUntil(destroy$),
          takeWhile(result => !result?.completed, true)
        )
        .subscribe(
          (result: any) => {
            if (!result) return;

            if (result.message) {
              this.emitStatus(statusSubject, recordingId, 'AI Processing...', result.message);
            }

            if (result.completed) {
              clearTimeout(timeout);
              this.aiService.isProcessing.next(false);
              
              this.emitStatus(statusSubject, recordingId, 'Completed', 'Processing completed successfully');
              
              // Store subscription for cleanup
              const activeRetry = this.activeRetries.get(recordingId);
              if (activeRetry) {
                activeRetry.aiSubscription = subscription;
              }
              
              // Note: We don't delete the recording here - it will be deleted when user clicks View button
              // This allows the user to see the View button and access the result
              
              // Return final result (same as ai-dialog's finalResult - line 263)
              resolve({
                finalResult: result.request,
                requestId: result.aiRequestId
              });
            } else if (result.error) {
              clearTimeout(timeout);
              this.aiService.isProcessing.next(false);
              reject(new Error(result.message || 'AI processing failed'));
            }
          },
          (error) => {
            clearTimeout(timeout);
            this.aiService.isProcessing.next(false);
            reject(error);
          }
        );
      
      // Store subscription for cleanup
      const activeRetry = this.activeRetries.get(recordingId);
      if (activeRetry) {
        activeRetry.aiSubscription = subscription;
      }
    });
  }

  private async compileTranscript(recordingId: string): Promise<string> {
    console.log(`[RetryService] Compiling transcript for recording ${recordingId}`);
    // Metadata only - compiling a transcript never needs the audio back.
    const segments = await this.recordingStorageService.getRecordingSegmentMetadata(recordingId);
    
    // Filter to segments that have been successfully transcribed (using stored transcripts from IndexedDB)
    // This includes segments that were transcribed before retry AND segments transcribed during retry
    const successfulSegments = segments
      .filter(seg => seg.processed && seg.transcript)
      .sort((a, b) => a.order - b.order);

    console.log(`[RetryService] Found ${successfulSegments.length} successful segments (with stored transcripts) out of ${segments.length} total`);
    
    // Log which segments are being used (for debugging)
    if (successfulSegments.length > 0) {
      console.log(`[RetryService] Using stored transcripts from segments:`, 
        successfulSegments.map(seg => ({ id: seg.id, order: seg.order, transcriptLength: seg.transcript?.length || 0 }))
      );
    }

    // Verify segments are in correct order before compiling
    const orders = successfulSegments.map(seg => seg.order);
    const isOrdered = orders.every((order, index) => index === 0 || order >= orders[index - 1]);
    if (!isOrdered) {
      console.warn(`[RetryService] ⚠️ Segments are not in correct order! Orders: ${orders.join(', ')}`);
    } else {
      console.log(`[RetryService] ✅ Segments are in correct order: ${orders.join(', ')}`);
    }

    let transcript = '';
    for (const segment of successfulSegments) {
      // Use the stored transcript from IndexedDB (includes both pre-existing and newly transcribed segments)
      // Segments are already sorted by order property above, so they're concatenated in the correct sequence
      transcript += (segment.transcript || '') + ' ';
    }

    const compiledTranscript = transcript.trim();
    console.log(`[RetryService] Compiled transcript: ${compiledTranscript.length} characters from ${successfulSegments.length} segment(s) in order`);
    return compiledTranscript;
  }

  private emitStatus(
    subject: Subject<RetryStatusUpdate>,
    recordingId: string,
    status: string,
    message: string
  ): void {
    subject.next({ recordingId, status, message });
  }

  // Check if a recording is currently being retried
  isRetrying(recordingId: string): boolean {
    return this.activeRetries.has(recordingId);
  }

  // Cancel an active retry
  cancelRetry(recordingId: string): void {
    const retry = this.activeRetries.get(recordingId);
    if (retry) {
      if (retry.subscription && typeof retry.subscription.unsubscribe === 'function') {
        retry.subscription.unsubscribe();
      }
      if (retry.transcriptionSubscription && typeof retry.transcriptionSubscription.unsubscribe === 'function') {
        retry.transcriptionSubscription.unsubscribe();
      }
      if (retry.aiSubscription && typeof retry.aiSubscription.unsubscribe === 'function') {
        retry.aiSubscription.unsubscribe();
      }
      retry.destroy$.next();
      retry.destroy$.complete();
      retry.statusSubject.complete();
      this.activeRetries.delete(recordingId);
    }
  }
}
